// Lightweight custom-SVG charts (§5.2). Deterministic, no animation reliance,
// so they screenshot identically in test mode.

import type { ProfileAggregates } from "../lib/types.ts";

const RATING_KEYS = ["0.5", "1", "1.5", "2", "2.5", "3", "3.5", "4", "4.5", "5"];

export function RatingHistogram({ hist }: { hist: Record<string, number> }) {
  const counts = RATING_KEYS.map((k) => hist[k] ?? 0);
  const max = Math.max(1, ...counts);
  const total = counts.reduce((a, b) => a + b, 0);
  const avg =
    total === 0 ? 0 : RATING_KEYS.reduce((s, k) => s + parseFloat(k) * (hist[k] ?? 0), 0) / total;
  const mode = RATING_KEYS[counts.indexOf(Math.max(...counts))];

  return (
    <div data-testid="chart-rating-hist">
      <div className="flex h-32 items-end gap-1.5">
        {RATING_KEYS.map((k, i) => {
          // Each column is full-height so the bar's percentage height resolves;
          // a small floor keeps non-zero bars visible.
          const pct = counts[i] === 0 ? 0 : Math.max((counts[i] / max) * 100, 4);
          const isMode = k === mode && counts[i] > 0;
          return (
            <div key={k} className="flex h-full flex-1 flex-col justify-end" title={`★${k}: ${counts[i]}`}>
              <div
                className={`w-full rounded-t ${isMode ? "bg-gold-500" : "bg-gold-500/45"}`}
                style={{ height: `${pct}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-ink-400">
        <span>★0.5</span>
        <span>★5</span>
      </div>
      <p className="mt-2 text-sm text-ink-400">
        Average <strong className="text-ink-900">★{avg.toFixed(2)}</strong> · most-given{" "}
        <strong className="text-ink-900">★{mode}</strong>
      </p>
    </div>
  );
}

export function DecadeBars({ byDecade, username }: { byDecade: Record<string, number>; username?: string }) {
  // Newest decade first (2020s at top, 1870s at the bottom).
  const decades = Object.keys(byDecade).sort((a, b) => Number(b) - Number(a));
  const max = Math.max(1, ...decades.map((d) => byDecade[d]));
  return (
    <div data-testid="chart-decades" className="space-y-1.5">
      {decades.map((d) => {
        const Row = (
          <>
            <span className="w-10 shrink-0 text-ink-400 tabular-nums group-hover:text-red-600">{d}s</span>
            <div className="h-3.5 flex-1 rounded bg-paper-100">
              <div className="h-full rounded bg-green-500" style={{ width: `${(byDecade[d] / max) * 100}%` }} />
            </div>
            <span className="w-8 shrink-0 text-right text-ink-400 tabular-nums">{byDecade[d]}</span>
          </>
        );
        return username ? (
          <a
            key={d}
            href={`https://letterboxd.com/${username}/films/decade/${d}s/`}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2 text-sm no-underline"
            title={`See ${d}s on Letterboxd`}
          >
            {Row}
          </a>
        ) : (
          <div key={d} className="flex items-center gap-2 text-sm">
            {Row}
          </div>
        );
      })}
    </div>
  );
}

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function HeatStrip({ loggedByMonth }: { loggedByMonth: Record<string, number> }) {
  // Full diary history as a year x month grid: one row per year, twelve month
  // columns. Empty months stay faint so gaps read as gaps.
  const keys = Object.keys(loggedByMonth).filter((k) => loggedByMonth[k] > 0);
  if (keys.length === 0) return <span className="text-sm text-ink-400">No diary data</span>;

  const years = keys.map((k) => Number(k.split("-")[0]));
  const minYear = Math.min(...years);
  const maxYear = Math.max(...years);
  const rows: number[] = [];
  for (let y = maxYear; y >= minYear; y--) rows.push(y);

  const max = Math.max(1, ...keys.map((k) => loggedByMonth[k]));
  const total = keys.reduce((s, k) => s + loggedByMonth[k], 0);
  const span = minYear === maxYear ? `${minYear}` : `${minYear}–${maxYear}`;

  return (
    <div data-testid="chart-heatstrip">
      <p className="mono-meta mb-2">
        {span} · {total.toLocaleString()} logged
      </p>
      <div className="overflow-x-auto">
        <div className="min-w-[280px]">
          {/* Month header */}
          <div className="mb-1 grid grid-cols-[2rem_repeat(12,1fr)] gap-1">
            <span />
            {MONTH_ABBR.map((mo) => (
              <span key={mo} className="text-center text-[8px] leading-none text-ink-400">
                {mo[0]}
              </span>
            ))}
          </div>
          {rows.map((year) => (
            <div key={year} className="mb-1 grid grid-cols-[2rem_repeat(12,1fr)] items-center gap-1">
              <span className="font-mono text-[10px] leading-none text-ink-500 tabular-nums">{year}</span>
              {MONTH_ABBR.map((_, monthIdx) => {
                const key = `${year}-${String(monthIdx + 1).padStart(2, "0")}`;
                const v = loggedByMonth[key] ?? 0;
                const intensity = v === 0 ? 0 : 0.18 + 0.82 * (v / max);
                return (
                  <div
                    key={key}
                    className="aspect-square w-full rounded-sm"
                    style={{
                      backgroundColor: v === 0 ? "var(--paper-100, #efe3d3)" : `rgba(232,148,90,${intensity})`,
                    }}
                    title={v === 0 ? `${MONTH_ABBR[monthIdx]} ${year}: none` : `${MONTH_ABBR[monthIdx]} ${year}: ${v} logged`}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-2 flex items-center justify-end gap-1.5 text-[9px] text-ink-400">
        <span>less</span>
        {[0, 0.35, 0.6, 0.85, 1].map((t) => (
          <span
            key={t}
            className="h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: t === 0 ? "var(--paper-100, #efe3d3)" : `rgba(232,148,90,${0.18 + 0.82 * t})` }}
          />
        ))}
        <span>more</span>
      </div>
    </div>
  );
}

export function ScoreDial({ score, size = 120, label }: { score: number; size?: number; label?: string }) {
  const r = size / 2 - 8;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score)) / 100;
  const color = score >= 85 ? "#75182A" : score >= 70 ? "#E8945A" : score >= 50 ? "#BC5A38" : "#8D7178";
  // Star labels (e.g. "★3.5") are wider than a bare number — shrink to fit.
  const text = label ?? String(score);
  const fontSize = size * (text.length > 3 ? 0.22 : 0.28);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid="score-dial">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E9D5BE" strokeWidth="8" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray={`${circ * pct} ${circ}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fill="currentColor" fontFamily="var(--font-display)" fontSize={fontSize} fontWeight="800">
        {text}
      </text>
    </svg>
  );
}

export function VennCounts({
  onlyA,
  both,
  onlyB,
  nameA,
  nameB,
}: {
  onlyA: number;
  both: number;
  onlyB: number;
  nameA: string;
  nameB: string;
}) {
  return (
    <svg viewBox="0 0 320 160" className="w-full max-w-md" data-testid="venn">
      <circle cx="120" cy="80" r="70" fill="#75182A" fillOpacity="0.16" stroke="#75182A" strokeOpacity="0.5" />
      <circle cx="200" cy="80" r="70" fill="#E8945A" fillOpacity="0.2" stroke="#C0703A" strokeOpacity="0.6" />
      <text x="80" y="85" textAnchor="middle" fill="#1F1114" fontFamily="var(--font-display)" fontSize="20" fontWeight="800">{onlyA}</text>
      <text x="160" y="85" textAnchor="middle" fill="#1F1114" fontFamily="var(--font-display)" fontSize="20" fontWeight="800">{both}</text>
      <text x="240" y="85" textAnchor="middle" fill="#1F1114" fontFamily="var(--font-display)" fontSize="20" fontWeight="800">{onlyB}</text>
      <text x="80" y="150" textAnchor="middle" fill="#8D7178" fontSize="11">{nameA}</text>
      <text x="240" y="150" textAnchor="middle" fill="#8D7178" fontSize="11">{nameB}</text>
    </svg>
  );
}

export function StatTile({ value, label, testid }: { value: string | number; label: string; testid?: string }) {
  return (
    <div className="card" data-testid={testid}>
      <div className="stat-num">{typeof value === "number" ? value.toLocaleString() : value}</div>
      <div className="stat-label mt-1">{label}</div>
    </div>
  );
}

export type { ProfileAggregates };
